// #1536: a revisited list or overview shows what it last loaded at once, while it refreshes.
//
// The last successful answer of each read below is kept in this tab's memory only; nothing is
// persisted. The rules that keep it from ever showing the wrong thing:
// - Only these reads are remembered. The request workspace, editors and delivery reconciliation
//   never read from here: a stale approve/send action or a late refresh over typed input is worse
//   than a loading state.
// - An answer is keyed by method, arguments and the owner's tenant context, and the session layer
//   clears everything on sign-in, sign-out, an ended session and a business switch. A `me` answer
//   naming another person, business or role, or a read refused as signed out, clears it too.
// - Every other client call may change data, so it clears the cache when it starts and again when
//   it settles. An answer that was in flight across a clear is dropped, never stored. The first
//   opening of a request moves it out of "Neu", so an opening that was recorded clears it as well.
// - An answer older than its maximum age is not shown.
//
// #1543: what a change cleared is read again once changes have been quiet for a moment, so the
// page the operator returns to already holds the answer from after the change. Only reads cleared
// by a change are read again, never after a session or business clear, and an answer a page has
// meanwhile read for itself is kept.
import type { CockpitClient } from "./client";
import { ApiError, getTenantContext } from "./http-client";

export const CACHED_READS = [
  "getOverviewMetrics",
  "getSetupState",
  "listConversations",
  "listFiledMessages",
  "listMailDomains",
  "listPlatformMailboxes",
  "listStaffInvitations",
  "listTeams",
  "listTenantMailboxes",
  "listTenants",
  "listTrustedForwarders",
  "listUsers",
  "presenceHeartbeat",
  "tenantCatalogArticles",
  "tenantCatalogStats",
  "tenantNeoMailbox",
  "tenantReadiness",
  "tenantUsage",
] as const satisfies readonly (keyof CockpitClient)[];

/**
 * Calls that only read, or only record that something was viewed or that the session is present.
 * Anything not named here or above counts as a change, including methods added later.
 */
const LEAVES_DATA_UNCHANGED = [
  "checkInvitationAddress",
  "claimDraftFirstView",
  "getAccountProfile",
  "getConfig",
  "getEmailQaRun",
  "getEmailQaStatus",
  "getEmailSignature",
  "getFeatureDefaults",
  "getFiledMessage",
  "getGooglePlacesQuota",
  "getHostEnvironment",
  "getMailboxRefresh",
  "getPositions",
  "getWorkflowBusiness",
  "getWorkflowProcessStatus",
  "getPresence",
  "getRequest",
  "getRequestCustomer",
  "getTenantFeatures",
  "getTenantHealthAlerts",
  "getTenantRuleParameters",
  "getBusinessRuleValues",
  "previewBusinessRuleValues",
  "listAssignees",
  "listCustomerMail",
  "listEmailQaRuns",
  "listInvitations",
  "listOwnTeamIds",
  "listPlatformMailboxDomains",
  "listPreviousRequests",
  "listRequestAssignees",
  "listRequests",
  "listThreadTargets",
  "previewWorkflowSwitch",
  "listUncertainDeliveries",
  "me",
  "neoMailboxAvailability",
  "neoMailboxOffer",
  "previewInvitation",
  "previewStaffInvitation",
  "previousMailDomainAddress",
  "subscribeConsoleEvents",
  "subscribeOwnerEvents",
  "testMailCatalog",
  "verifyPasswordReset",
] as const satisfies readonly (keyof CockpitClient)[];

type CachedRead = (typeof CACHED_READS)[number];
type ReadArgs<K extends CachedRead> = Parameters<CockpitClient[K]>;
type ReadAnswer<K extends CachedRead> = Awaited<ReturnType<CockpitClient[K]>>;

const MAX_AGE_MS = 15 * 60_000;
// Presence ages within a minute: an older roster would show colleagues online who have left.
const MAX_AGE_BY_READ: Partial<Record<CachedRead, number>> = { presenceHeartbeat: 60_000 };
const MAX_ENTRIES = 200;
/** Quiet time after the last change before its cleared reads are read again. */
const REFILL_DELAY_MS = 1_000;
/** The most recently answered reads a change cleared; older ones load when their page opens. */
const REFILL_MAX_READS = 12;
/** A heartbeat records presence; it is only sent from the page that shows it. */
const NEVER_REFILLED: ReadonlySet<string> = new Set<CachedRead>(["presenceHeartbeat"]);

interface Read {
  key: string;
  method: CachedRead;
  args: readonly unknown[];
}

const cachedReads: ReadonlySet<string> = new Set(CACHED_READS);
const unchanging: ReadonlySet<string> = new Set([...CACHED_READS, ...LEAVES_DATA_UNCHANGED]);
const answers = new Map<string, Read & { at: number; value: unknown }>();
// The shell can ask for assignment/arrival snapshots several times during navigation. Keep one
// request in flight and one trailing refresh; these answers are never reused after completion.
const requestListsInFlight = new Map<string, { promise: Promise<unknown>; repeat: boolean }>();
let generation = 0;
/** Who the last `me` answer named; undefined before the first one. */
let identity: string | null | undefined;
/** The client refills read through; the one `withReadCache` wraps. */
let refillClient: CockpitClient | null = null;
/** Reads a change cleared that are still to be read again, oldest first. */
let refillQueue: Read[] = [];
/** Refill reads on their way, so a change that clears them queues them again. */
const refilling = new Map<string, Read>();
let refillTimer: ReturnType<typeof setTimeout> | null = null;
let changesInFlight = 0;

function keyOf(method: string, args: readonly unknown[]): string {
  // `list()` and `list(undefined)` are the same read.
  let length = args.length;
  while (length > 0 && args[length - 1] === undefined) length -= 1;
  return JSON.stringify([getTenantContext(), method, args.slice(0, length)]);
}

function maxAge(method: CachedRead): number {
  return MAX_AGE_BY_READ[method] ?? MAX_AGE_MS;
}

/** The remembered answer to exactly this read in the current context, if a recent one exists. */
export function cachedRead<K extends CachedRead>(method: K, ...args: ReadArgs<K>): ReadAnswer<K> | undefined {
  const key = keyOf(method, args);
  const entry = answers.get(key);
  if (!entry) return undefined;
  if (Date.now() - entry.at > maxAge(method)) {
    answers.delete(key);
    return undefined;
  }
  return entry.value as ReadAnswer<K>;
}

/** Moves on with every clear: a read started before it may predate a change or a switch (#2302). */
export function readCacheGeneration(): number {
  return generation;
}

function forget(): void {
  generation += 1;
  answers.clear();
  requestListsInFlight.clear();
}

/** Forgets every answer, and every answer still on its way, and reads nothing again. */
export function clearReadCache(): void {
  forget();
  refillQueue = [];
  refilling.clear();
  if (refillTimer !== null) clearTimeout(refillTimer);
  refillTimer = null;
}

/** Clears for a change, keeping what it clears to be read again once changes are quiet. */
function clearForChange(): void {
  const now = Date.now();
  const cleared = [
    ...refilling.values(),
    ...[...answers.values()].filter((entry) => now - entry.at <= maxAge(entry.method)),
  ].filter((read) => !NEVER_REFILLED.has(read.method));
  forget();
  refilling.clear();
  const keys = new Set(cleared.map((read) => read.key));
  refillQueue = [...refillQueue.filter((read) => !keys.has(read.key)), ...cleared].slice(-REFILL_MAX_READS);
}

function scheduleRefill(): void {
  if (refillTimer !== null) clearTimeout(refillTimer);
  refillTimer = null;
  if (changesInFlight > 0 || refillQueue.length === 0) return;
  refillTimer = setTimeout(refill, REFILL_DELAY_MS);
}

function refill(): void {
  refillTimer = null;
  const client = refillClient;
  if (changesInFlight > 0 || client === null) return;
  const reads = refillQueue.reverse();
  refillQueue = [];
  const started = generation;
  for (const read of reads) {
    // A page that read this for itself already holds the newer answer; a business switched in
    // the meantime would store it under a context it was not read for.
    if (answers.has(read.key) || keyOf(read.method, read.args) !== read.key) continue;
    refilling.set(read.key, read);
    let pending: Promise<unknown>;
    try {
      pending = Promise.resolve((client[read.method] as (...args: unknown[]) => unknown).apply(client, [...read.args]));
    } catch (error) {
      pending = Promise.reject(error);
    }
    pending.then(
      (value) => {
        if (refilling.get(read.key) !== read) return;
        refilling.delete(read.key);
        if (generation === started && !answers.has(read.key)) remember(read, value);
      },
      (error: unknown) => {
        if (refilling.get(read.key) === read) refilling.delete(read.key);
        if (error instanceof ApiError && error.status === 401) clearReadCache();
      },
    );
  }
}

function remember(read: Read, value: unknown): void {
  answers.delete(read.key);
  answers.set(read.key, { ...read, at: Date.now(), value });
  if (answers.size > MAX_ENTRIES) {
    const oldest = answers.keys().next();
    if (!oldest.done) answers.delete(oldest.value);
  }
}

function observeIdentity(user: unknown): void {
  const next = user && typeof user === "object"
    ? JSON.stringify(["id", "tenantId", "role"].map((field) => (user as Record<string, unknown>)[field] ?? null))
    : null;
  if (identity !== undefined && next !== identity) clearReadCache();
  identity = next;
}

/** The client as screens use it: remembered reads are stored, any other call clears them. */
export function withReadCache(client: CockpitClient): CockpitClient {
  refillClient = client;
  return new Proxy(client, {
    get(target, property, receiver) {
      const member: unknown = Reflect.get(target, property, receiver);
      if (typeof property !== "string" || typeof member !== "function") return member;
      const call = member as (...args: unknown[]) => unknown;

      if (property === "listRequests") return (...args: unknown[]) => {
        const key = keyOf(property, args), started = generation;
        const existing = requestListsInFlight.get(key);
        if (existing) { existing.repeat = true; return existing.promise; }
        const read = { repeat: false, promise: Promise.resolve<unknown>(undefined) };
        read.promise = (async () => {
          let answer: unknown;
          do { read.repeat = false; answer = await call.apply(target, args); }
          while (read.repeat && generation === started && keyOf(property, args) === key);
          return answer;
        })().finally(() => { if (requestListsInFlight.get(key) === read) requestListsInFlight.delete(key); });
        requestListsInFlight.set(key, read);
        return read.promise;
      };

      if (cachedReads.has(property)) {
        return (...args: unknown[]) => {
          const read: Read = { key: keyOf(property, args), method: property as CachedRead, args };
          const started = generation;
          const pending = call.apply(target, args) as Promise<unknown>;
          void Promise.resolve(pending).then(
            (value) => {
              if (generation === started) remember(read, value);
            },
            (error: unknown) => {
              if (error instanceof ApiError && error.status === 401) clearReadCache();
            },
          );
          return pending;
        };
      }

      if (property === "me" || property === "recordRequestOpening" || property === "recordFiledMessageOpening") {
        return (...args: unknown[]) => {
          const pending = call.apply(target, args) as Promise<unknown>;
          void Promise.resolve(pending).then(
            (answer) => {
              if (property === "me") observeIdentity(answer);
              else if ((answer as { recorded?: boolean } | null)?.recorded === true) {
                clearForChange();
                scheduleRefill();
              }
            },
            () => undefined,
          );
          return pending;
        };
      }

      // Returned as it is, so a spy installed on the client is still the function read back. No
      // client method uses `this`.
      if (unchanging.has(property)) return member;

      return (...args: unknown[]) => {
        clearForChange();
        changesInFlight += 1;
        const settled = () => {
          changesInFlight -= 1;
          clearForChange();
          scheduleRefill();
        };
        let result: unknown;
        try {
          result = call.apply(target, args);
        } catch (error) {
          settled();
          throw error;
        }
        if (result instanceof Promise) {
          void result.finally(settled).catch(() => undefined);
        } else {
          settled();
        }
        return result;
      };
    },
  });
}
